import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageTitle from "./include/PageTitle";
import Loader from "../Components/Loader";
import api from "../Utils/Axios";
import { API_BASES, fetchApiJson } from "../config/apiBase";
import { renderBlogContent } from "../Utils/blogContent";
import { useI18n } from "../i18n/I18nContext";
import Seo from "../Components/Seo";

const fallbackCover = "/img/blog/1.jpg";

const formatDate = (dateValue, locale = undefined) => {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(locale === "ar" ? "ar-SA" : undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const resolveImage = (url) => {
  if (!url) return fallbackCover;
  if (/^data:image\//i.test(url)) return url;
  if (/^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\/uploads\//i.test(url)) {
    return `${api.defaults.baseURL}${new URL(url).pathname}`;
  }
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/uploads/")) return `${api.defaults.baseURL}${url}`;
  return url.startsWith("/") ? url : `/${url}`;
};

const handleImageFallback = (event) => {
  if (event.currentTarget.src.includes(fallbackCover)) return;
  event.currentTarget.src = fallbackCover;
};

const blogDetailsCopy = {
  en: {
    newsDetails: "News Details",
    loadingBlog: "Loading blog...",
    inquiryForm: "Inquiry Form",
    yourName: "Your Name*",
    yourEmail: "Your e-Mail*",
    mobileNumber: "Mobile Number*",
    writeInquiry: "Write your inquiry...",
    sending: "Sending...",
    sendInquiry: "Send Inquiry",
    inquirySent: "Inquiry sent successfully.",
    inquiryFailed: "Failed to send inquiry.",
    searchObject: "Search Object",
    searchKeyword: "Search your keyword...",
    noSearchMatch: "No blog posts match this search.",
    featuredPropertiesTitle: "Buy Properties In Eastern Region (Dammam, Khobar, Jubail, Dhahran)",
    topCategories: "Top Categories",
    latestBlogs: "Latest Blogs",
    noFeatured: "No featured properties available right now.",
    realEstate: "Real Estate",
    contactForPrice: "Contact for price",
    clickForNegotiation: "Click for negotiation",
  },
  ar: {
    newsDetails: "تفاصيل الخبر",
    loadingBlog: "جاري تحميل الخبر...",
    inquiryForm: "نموذج الاستفسار",
    yourName: "الاسم*",
    yourEmail: "البريد الإلكتروني*",
    mobileNumber: "رقم الجوال*",
    writeInquiry: "اكتب استفسارك...",
    sending: "جاري الإرسال...",
    sendInquiry: "إرسال الاستفسار",
    inquirySent: "تم إرسال الاستفسار بنجاح.",
    inquiryFailed: "فشل إرسال الاستفسار.",
    searchObject: "البحث في الأخبار",
    searchKeyword: "اكتب كلمة البحث...",
    noSearchMatch: "لا توجد أخبار مطابقة لهذا البحث.",
    featuredPropertiesTitle: "عقارات مميزة في المنطقة الشرقية، الدمام، الخبر، الجبيل، والظهران",
    topCategories: "التصنيفات",
    latestBlogs: "أحدث الأخبار",
    noFeatured: "لا توجد عقارات مميزة حالياً.",
    realEstate: "عقارات",
    contactForPrice: "تواصل لمعرفة السعر",
    clickForNegotiation: "اضغط للتفاوض",
  },
};

const normalizeCollection = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.properties)) return payload.properties;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const isFeaturedProperty = (property) => {
  const value = property?.isFeatured;
  return value === true || value === "true" || value === 1 || value === "1";
};

const formatPrice = (property, ui) => {
  if (!property) return ui.contactForPrice;
  if (property.priceDisplayMode === "NEGOTIATION") return ui.clickForNegotiation;
  if (property.price == null) return ui.contactForPrice;
  return `${Number(property.price).toLocaleString()} ${property.currency || "SAR"}`;
};

const BlogsDetails = () => {
  const { locale } = useI18n();
  const { id } = useParams();
  const slug = id;
  const [blog, setBlog] = useState(null);
  const [latestBlogs, setLatestBlogs] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [inquiryForm, setInquiryForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const localizedBlog = blog;
  const localizedLatestBlogs = latestBlogs;
  const localizedProperties = properties;
  const ui = blogDetailsCopy[locale] || blogDetailsCopy.en;

  useEffect(() => {
    let ignore = false;
    const loadPageData = async () => {
      try {
        setLoading(true);
        setError("");
        const blogResponse = await api.get(`/api/blogs/${slug}?lang=${locale}`);

        if (!ignore) {
          setBlog(blogResponse?.data?.data || null);
        }

        const [latestBlogsResult, propertiesResult] = await Promise.allSettled([
          fetchApiJson(`/api/blogs?limit=12&lang=${locale}`),
          fetchApiJson(`/api/properties?lang=${locale}`),
        ]);

        if (!ignore) {
          setLatestBlogs(
            latestBlogsResult.status === "fulfilled" &&
              normalizeCollection(latestBlogsResult.value?.json)
              ? normalizeCollection(latestBlogsResult.value?.json)
              : []
          );

          setProperties(
            propertiesResult.status === "fulfilled" &&
              normalizeCollection(propertiesResult.value?.json)
              ? normalizeCollection(propertiesResult.value?.json)
              : []
          );
        }
      } catch (err) {
        if (!ignore) {
          setError(
            err?.response?.data?.message ||
              err?.message ||
              `Could not load blog data from any configured API base: ${API_BASES.join(", ")}`
          );
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    if (slug) loadPageData();
    return () => {
      ignore = true;
    };
  }, [slug, locale]);

  const handleInquiryChange = (field) => (event) => {
    setInquiryForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleInquirySubmit = async (event) => {
    event.preventDefault();
    if (!slug) return;

    try {
      setSubmitting(true);
      setFeedback({ type: "", message: "" });
      const { json } = await fetchApiJson(`/api/blogs/${slug}/inquiries`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(inquiryForm),
      });
      if (json?.success === false) {
        throw new Error(json?.message || ui.inquiryFailed);
      }
      setInquiryForm({
        name: "",
        email: "",
        phone: "",
        message: "",
      });
      setFeedback({ type: "success", message: json?.message || ui.inquirySent });
    } catch (err) {
      setFeedback({
        type: "error",
        message: err?.message || err?.response?.data?.message || ui.inquiryFailed,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const latestSidebarBlogs = useMemo(
    () => localizedLatestBlogs.filter((item) => item.slug !== slug).slice(0, 3),
    [localizedLatestBlogs, slug]
  );

  const filteredSearchBlogs = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return localizedLatestBlogs.slice(0, 5);
    return localizedLatestBlogs
      .filter((item) => item.title?.toLowerCase().includes(query) || item.excerpt?.toLowerCase().includes(query))
      .slice(0, 5);
  }, [localizedLatestBlogs, searchTerm]);

  const topCategories = useMemo(() => {
    const counts = new Map();
    localizedLatestBlogs.forEach((entry) => {
      (entry.tags || []).forEach((tag) => {
        const key = tag.slug || tag.name;
        if (!key) return;
        const current = counts.get(key) || { ...tag, total: 0 };
        current.total += 1;
        counts.set(key, current);
      });
    });

    return Array.from(counts.values())
      .sort((a, b) => b.total - a.total || String(a.name).localeCompare(String(b.name)))
      .slice(0, 6);
  }, [localizedLatestBlogs]);

  const featuredProperties = useMemo(() => {
    return localizedProperties
      .filter(isFeaturedProperty)
      .sort((left, right) => new Date(right.publishedAt || right.createdAt || 0) - new Date(left.publishedAt || left.createdAt || 0))
      .slice(0, 5);
  }, [localizedProperties]);

    return (
        <div>
            <Seo
              title={localizedBlog?.title ? `${localizedBlog.title} | QTECH` : "News Details | QTECH"}
              titleAr={localizedBlog?.title ? `${localizedBlog.title} | شركة تقنيات الجودة` : "تفاصيل الخبر | شركة تقنيات الجودة"}
              description={localizedBlog?.excerpt || "Real estate news and insights from QTECH in Saudi Arabia."}
              descriptionAr={localizedBlog?.excerpt || "أخبار ورؤى عقارية من شركة تقنيات الجودة في السعودية والخبر."}
              keywordsAr="أخبار عقارية، السوق العقاري السعودي، عقارات الخبر، استثمار عقاري، تسويق عقاري، إدارة أملاك، شركة تقنيات الجودة"
              path={`/blogs-details/${slug || ""}`}
              image={resolveImage(localizedBlog?.coverImageUrl)}
              type="article"
              structuredData={localizedBlog ? {
                "@context": "https://schema.org",
                "@type": "Article",
                headline: localizedBlog.title,
                description: localizedBlog.excerpt,
                image: resolveImage(localizedBlog.coverImageUrl),
                datePublished: localizedBlog.publishedAt || localizedBlog.createdAt,
                dateModified: localizedBlog.updatedAt || localizedBlog.publishedAt || localizedBlog.createdAt,
                author: { "@type": "Organization", name: "QTECH" },
                publisher: { "@type": "Organization", name: "QTECH" },
              } : undefined}
            />
      <PageTitle title={ui.newsDetails} currentPage={ui.newsDetails} bgImage="/img/bg/14.jpg" />

            <div className="ltn__page-details-area ltn__blog-details-area mb-120">
                <div className="container">
          {loading ? <Loader label={ui.loadingBlog} /> : null}
          {!loading && error ? <div className="alert alert-danger">{error}</div> : null}

          {!loading && !error && localizedBlog ? (
                    <div className="row">
                        <div className="col-lg-8">
                            <div className="ltn__blog-details-wrap">
                                <div className="ltn__page-details-inner ltn__blog-details-inner">
                                    <div className="ltn__blog-meta">
                                        <ul>
                                            <li className="ltn__blog-category">
                          <a href="#!">{localizedBlog.tags?.[0]?.name || ui.realEstate}</a>
                                            </li>
                                            <li className="ltn__blog-date">
                          <i className="far fa-calendar-alt" /> {formatDate(localizedBlog.publishedAt, locale)}
                                            </li>
                                        </ul>
                                    </div>

                    <h2 className="ltn__blog-title">{localizedBlog.title}</h2>
                    <img
                      className="qtech-blog-detail-image"
                      src={resolveImage(localizedBlog.coverImageUrl)}
                      alt={localizedBlog.title || "QTECH real estate blog"}
                      onError={handleImageFallback}
                    />
                    {localizedBlog.excerpt ? <p className="mt-20">{localizedBlog.excerpt}</p> : null}
                    <div className="mt-20">{renderBlogContent(localizedBlog.contentHtml)}</div>
                                    </div>

                  <div className="ltn__comment-reply-area ltn__form-box mt-50">
                    <h4 className="title-2">{ui.inquiryForm}</h4>
                    <form onSubmit={handleInquirySubmit}>
                      <div className="input-item input-item-name ltn__custom-icon">
                        <input
                          type="text"
                          placeholder={ui.yourName}
                          value={inquiryForm.name}
                          onChange={handleInquiryChange("name")}
                        />
                      </div>
                      <div className="input-item input-item-email ltn__custom-icon">
                        <input
                          type="email"
                          placeholder={ui.yourEmail}
                          value={inquiryForm.email}
                          onChange={handleInquiryChange("email")}
                        />
                      </div>
                      <div className="input-item input-item-phone ltn__custom-icon">
                        <input
                          type="text"
                          placeholder={ui.mobileNumber}
                          value={inquiryForm.phone}
                          onChange={handleInquiryChange("phone")}
                        />
                      </div>
                      <div className="input-item input-item-textarea ltn__custom-icon">
                        <textarea
                          placeholder={ui.writeInquiry}
                          value={inquiryForm.message}
                          onChange={handleInquiryChange("message")}
                        />
                      </div>
                      {feedback.message ? (
                        <p className={feedback.type === "error" ? "text-danger" : "text-success"}>
                          {feedback.message}
                        </p>
                      ) : null}
                      <div className="btn-wrapper">
                        <button
                          className="btn theme-btn-1 btn-effect-1 text-uppercase"
                          type="submit"
                          disabled={submitting}
                        >
                          {submitting ? ui.sending : ui.sendInquiry}
                        </button>
                      </div>
                    </form>
                  </div>
                    </div>
                </div>
                <div className="col-lg-4">
                  <aside className="sidebar ltn__right-sidebar">
                    <div className="widget ltn__search-widget">
                      <h4 className="ltn__widget-title ltn__widget-title-border-2">{ui.searchObject}</h4>
                      <form onSubmit={(event) => event.preventDefault()}>
                        <input
                          type="text"
                          placeholder={ui.searchKeyword}
                          value={searchTerm}
                          onChange={(event) => setSearchTerm(event.target.value)}
                        />
                        <button type="submit">
                          <i className="fas fa-search" />
                        </button>
                      </form>
                      {filteredSearchBlogs.length > 0 ? (
                        <ul className="mt-3">
                          {filteredSearchBlogs.map((item) => (
                            <li key={item.id || item.slug} className="mb-2">
                              <Link to={`/blogs-details/${item.slug}`}>{item.title}</Link>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="mt-3 mb-0">{ui.noSearchMatch}</p>
                      )}
                    </div>

                    {/* <div className="widget ltn__form-widget">
                      <h4 className="ltn__widget-title ltn__widget-title-border-2">Drop Message For Book</h4>
                      <form onSubmit={handleInquirySubmit}>
                        <input
                          type="text"
                          placeholder="Your Name*"
                          value={inquiryForm.name}
                          onChange={handleInquiryChange("name")}
                        />
                        <input
                          type="email"
                          placeholder="Your e-Mail*"
                          value={inquiryForm.email}
                          onChange={handleInquiryChange("email")}
                        />
                        <input
                          type="text"
                          placeholder="Mobile Number*"
                          value={inquiryForm.phone}
                          onChange={handleInquiryChange("phone")}
                        />
                        <textarea
                          placeholder="Write Message..."
                          value={inquiryForm.message}
                          onChange={handleInquiryChange("message")}
                        />
                        {feedback.message ? (
                          <p className={feedback.type === "error" ? "text-danger" : "text-success"}>{feedback.message}</p>
                        ) : null}
                        <button className="btn theme-btn-1" type="submit" disabled={submitting}>
                          {submitting ? "Sending..." : "Send Message"}
                        </button>
                      </form>
                    </div> */}

                    <div className="widget ltn__top-rated-product-widget">
                      <h4 className="ltn__widget-title ltn__widget-title-border-2">{ui.featuredPropertiesTitle}</h4>
                      {featuredProperties.length > 0 ? (
                        <ul>
                          {featuredProperties.map((property) => (
                            <li key={property.id}>
                              <div className="top-rated-product-item clearfix">
                                <div className="top-rated-product-img">
                                  <Link to={`/property/${property.id}`}>
                                    <img
                                      src={resolveImage(property.coverImage || property.image)}
                                      alt={property.title || ui.realEstate}
                                      onError={handleImageFallback}
                                    />
                                  </Link>
                                </div>
                                <div className="top-rated-product-info">
                                  <h6>
                                    <Link to={`/property/${property.id}`}>{property.title || ui.realEstate}</Link>
                                  </h6>
                                  <div className="product-price">{formatPrice(property, ui)}</div>
                                </div>
                              </div>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="mb-0">{ui.noFeatured}</p>
                      )}
                    </div>

                    <div className="widget ltn__menu-widget">
                      <h4 className="ltn__widget-title ltn__widget-title-border-2">{ui.topCategories}</h4>
                      <ul>
                        {topCategories.map((tag) => (
                          <li key={tag.slug || tag.name}>
                            <a href="#!" onClick={(event) => event.preventDefault()}>
                              {tag.name} <span>({tag.total})</span>
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="widget ltn__popular-post-widget">
                      <h4 className="ltn__widget-title ltn__widget-title-border-2">{ui.latestBlogs}</h4>
                      <ul>
                        {latestSidebarBlogs.map((item) => (
                          <li key={item.id || item.slug}>
                            <div className="popular-post-widget-item clearfix">
                              <div className="popular-post-widget-img">
                                <Link to={`/blogs-details/${item.slug}`}>
                                  <img
                                    src={resolveImage(item.coverImageUrl)}
                                    alt={item.title || "Blog"}
                                    onError={handleImageFallback}
                                  />
                                </Link>
                              </div>
                              <div className="popular-post-widget-brief">
                                <h6>
                                  <Link to={`/blogs-details/${item.slug}`}>{item.title}</Link>
                                </h6>
                                <div className="ltn__blog-meta">
                                  <ul>
                                    <li className="ltn__blog-date">
                                      <i className="far fa-calendar-alt" />
                                      {formatDate(item.publishedAt, locale)}
                                    </li>
                                  </ul>
                                </div>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>

                  </aside>
                </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default BlogsDetails;
